<script lang="ts">
import { defineComponent as DO_defineComponent } from 'vue';
export default /*#__PURE__*/ DO_defineComponent({
	name: 'ToTransfer',
	componentName: 'ToTransfer'
});
</script>
<template>
	<div :key="langKey" :flex="flex ? flex : null" class="to-transfer" :class="setClass">
		<div class="to-transfer-panel">
			<span class="to-strong"
				><to-checkbox
					v-model="dataGlobal"
					:label="leftTitle || t('to.transfer.pending')"
					width="full"
					@change="
						val => {
							selectDataMap(val, 'dataGroup')
						}
					"
				></to-checkbox
			></span>
			<to-padding v-if="filterable" position="u">
				<to-input v-model="dataFilterKey" clearable width="full" :placeholder="t('to.transfer.placeholder')"></to-input>
			</to-padding>
			<to-scroll :height="height">
				<to-gap :value="0.5"></to-gap>
				<div v-if="dataMap && dataMap.length > 0">
					<to-checkbox v-for="(item, index) in dataMap" v-show="showData(item)" :key="index" v-model="item.checked" :disabled="setDataReadonly(item)" width="full" :label="item[labelText]"> </to-checkbox>
				</div>
				<to-padding v-else v-align="'center'" v-color="'info'" value="1.5">{{ t('to.transfer.noData') }}</to-padding>
				<to-gap :value="0.5"></to-gap>
			</to-scroll>
		</div>
		<to-gap inline></to-gap>
		<div class="to-transfer-btns">
			<to-button v-dis="dataChecked < 1" icon="right" mode="none" color="primary" @click="add"></to-button>
			<to-gap :value="0.5"></to-gap>
			<to-button v-dis="valueChecked < 1" icon="left" mode="none" color="primary" @click="remove"></to-button>
		</div>
		<to-gap inline></to-gap>
		<div class="to-transfer-panel">
			<span class="to-strong"
				><to-checkbox
					v-model="valueGlobal"
					:label="rightTitle || t('to.transfer.selected')"
					width="full"
					@change="
						val => {
							selectDataMap(val, 'valueGroup')
						}
					"
				></to-checkbox
			></span>
			<to-padding v-if="filterable" position="u">
				<to-input v-model="valueFilterKey" clearable width="full" :placeholder="t('to.transfer.placeholder')"></to-input>
			</to-padding>
			<to-scroll :height="height">
				<to-gap :value="0.5"></to-gap>
				<div v-if="valueMap && valueMap.length > 0">
					<to-checkbox v-for="(item, index) in valueMap" v-show="showValue(item)" :key="index" v-model="item.checked" :disabled="setValueReadonly(item)" width="full" :label="item[labelText]"></to-checkbox>
				</div>
				<to-padding v-else v-align="'center'" v-color="'info'" value="1.5">{{ t('to.transfer.noData') }}</to-padding>
				<to-gap :value="0.5"></to-gap>
			</to-scroll>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, computed, nextTick } from 'vue'
import type { Ref } from 'vue'
import { ToScroll, ToGap, ToPadding, ToCheckbox, ToInput, ToButton } from '@touchvue/components'
import { t, langKey } from '@touchvue/locale'

// 定义组件名称


// Props 定义
interface DataItem {
	[key: string]: any
}

interface Props {
	flex?: boolean
	mode?: string
	data?: DataItem[] | string
	leftTitle?: string
	rightTitle?: string
	modelValue?: string
	labelText?: string
	valueText?: string
	height?: string | number
	order?: string
	filterable?: boolean
	showDisabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
	flex: false,
	mode: 'default',
	data: () => '',
	leftTitle: '',
	rightTitle: '',
	modelValue: '',
	labelText: 'label',
	valueText: 'value',
	height: 16,
	order: 'push',
	filterable: true,
	showDisabled: false
})

// Emits 定义
const emit = defineEmits<{
	(e: 'update:modelValue', value: string): void
	(e: 'change', value: string): void
}>()

// 响应式数据
const iData: Ref<DataItem[]> = ref([])
const dataGlobal = ref(false)
const dataMap: Ref<DataItem[]> = ref([])
const dataChecked = computed(() => {
	let length = 0
	if (props.showDisabled) {
		length = dataMap.value.filter(item => item.checked && !item.disabled).length
	} else {
		length = dataMap.value.filter(item => item.checked).length
	}
	return length
})
const dataFilterKey = ref('')
const iValue = ref(props.modelValue)
const valueGlobal = ref(false)
const valueMap: Ref<DataItem[]> = ref([])
const valueChecked = computed(() => {
	return valueMap.value.filter(item => item.checked).length
})
const valueFilterKey = ref('')
const stopChange = ref(false)

// 监听器
watch(
	() => props.data,
	() => {
		transData()
		setData()
	},
	{ deep: true }
)

watch(
	() => props.modelValue,
	val => {
		iValue.value = val || ''
		if (stopChange.value) {
			return false
		}
		setData()
	}
)

onMounted(() => {
	transData()
	setData()
})

const setClass = computed(() => {
	const classes: string[] = []

	if (props.mode) classes.push(`mode-${props.mode}`)

	return classes
})

// 方法定义
// 转换 data 格式
const transData = () => {
	if (typeof props.data === 'string') {
		if (props.data) {
			const arr: DataItem[] = []
			props.data.split(',').map(item => {
				arr.push({
					[props.labelText]: item,
					[props.valueText]: item,
					checked: false
				})
			})
			iData.value = arr
		} else {
			iData.value = []
		}
	} else {
		let arr = props.data.map(item => {
			return { ...item, checked: item.checked || false }
		})
		iData.value = JSON.parse(JSON.stringify(arr))
	}
}

// 设置数据以匹配视图需要
const setData = () => {
	const dataMapArr: DataItem[] = []
	const valueMapArr: DataItem[] = []
	const valueArr = iValue.value.split(',')

	// 直接分发数据
	iData.value.map(item => {
		if (!valueArr.includes(item[props.valueText])) {
			dataMapArr.push(item)
		} else {
			if (props.showDisabled) {
				dataMapArr.push({ ...item, checked: true, disabled: true })
			}
		}
		if (valueArr.includes(item[props.valueText]) && props.order === 'original') {
			valueMapArr.push(item)
		}
	})

	// 按堆/栈方式排序选择结果
	if (iValue.value && props.order !== 'original') {
		iValue.value.split(',').map(item => {
			if (!iData.value.find(item2 => item2[props.valueText] === item)) {
				return
			}
			if (props.order === 'unshift') {
				valueMapArr.unshift({
					[props.labelText]: iData.value.find(item2 => item2[props.valueText] === item)[props.labelText],
					[props.valueText]: item,
					disabled: !!iData.value.find(item2 => item2[props.valueText] === item).disabled,
					checked: iData.value.find(item2 => item2[props.valueText] === item).checked
				})
			} else {
				valueMapArr.push({
					[props.labelText]: iData.value.find(item2 => item2[props.valueText] === item)[props.labelText],
					[props.valueText]: item,
					disabled: !!iData.value.find(item2 => item2[props.valueText] === item).disabled,
					checked: iData.value.find(item2 => item2[props.valueText] === item).checked
				})
			}
		})
	}

	// 设置数据
	dataMap.value = dataMapArr
	valueMap.value = valueMapArr
}

// 数据过滤
const showData = (item: DataItem) => {
	if (dataFilterKey.value) {
		if (item[props.labelText].includes(dataFilterKey.value)) {
			return true
		} else {
			return false
		}
	} else {
		return true
	}
}

// 值过滤
const showValue = (item: DataItem) => {
	if (valueFilterKey.value) {
		if (item[props.labelText].includes(valueFilterKey.value)) {
			return true
		} else {
			return false
		}
	} else {
		return true
	}
}

// 向右
const add = () => {
	if (props.showDisabled) {
		let arr = dataMap.value
			.filter(el => el.checked && !el.disabled)
			.map(item => {
				return {
					...item,
					checked: false
				}
			})
		valueMap.value = [...valueMap.value, ...arr]
		dataMap.value.forEach(item => {
			if (item.checked) {
				item.disabled = true
			}
		})
	} else {
		let arr = dataMap.value
			.filter(el => el.checked)
			.map(item => {
				return {
					...item,
					checked: false
				}
			})
		valueMap.value = [...valueMap.value, ...arr]
		arr.forEach(el => {
			let idx = dataMap.value.findIndex(el2 => el2[props.valueText] === el[props.valueText])
			dataMap.value.splice(idx, 1)
		})
	}
	stopChange.value = true
	setTimeout(() => {
		stopChange.value = false
	}, 10)
	nextTick(() => {
		let val = valueMap.value.map(item => item[props.valueText]).join(',')
		emit('update:modelValue', val)
		emit('change', val)
	})
}

// 向左
const remove = () => {
	let arr = valueMap.value
		.filter(el => el.checked)
		.map(item => {
			return {
				...item,
				checked: false
			}
		})
	if (props.showDisabled) {
		arr.forEach(item => {
			dataMap.value.forEach(el => {
				if (el[props.valueText] === item[props.valueText]) {
					el.disabled = false
					el.checked = false
				}
			})
		})
	} else {
		dataMap.value = [...dataMap.value, ...arr]
	}
	arr.forEach(el => {
		let idx = valueMap.value.findIndex(el2 => el2[props.valueText] === el[props.valueText])
		valueMap.value.splice(idx, 1)
	})
	stopChange.value = true
	setTimeout(() => {
		stopChange.value = false
	}, 10)
	nextTick(() => {
		let val = valueMap.value.map(item => item[props.valueText]).join(',')
		emit('update:modelValue', val)
		emit('change', val)
	})
}

// 全选/反选
const selectDataMap = (val: boolean, group: string) => {
	const targetGroup = group === 'dataGroup' ? dataMap.value : valueMap.value
	if (val) {
		targetGroup.forEach((item: DataItem) => {
			if (!item.disabled) {
				item.checked = true
			}
		})
	} else {
		targetGroup.forEach((item: DataItem) => {
			if (!item.disabled) {
				item.checked = false
			}
		})
	}
}

const setDataReadonly = (item: DataItem) => {
	if (item.disabled) {
		return true
	} else {
		if (showData(item)) {
			return false
		} else {
			return true
		}
	}
}

const setValueReadonly = (item: DataItem) => {
	if (item.disabled) {
		return true
	} else {
		if (showValue(item)) {
			return false
		} else {
			return true
		}
	}
}
</script>
